import { useCallback, useEffect, useState } from "react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Loader2, Lock, Settings, Tags, Sparkles, X } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import type { SubcategoryDetailProps, Subcategory } from "../../constants/settingsStructure";
import { useSettingsContext } from "../../context/SettingsContext";
import { OpenAIKeyModal } from "../../components/OpenAIKeyModal";

import { SMMGeneratorProvider, useSMMGeneratorContext } from "./context/SMMGeneratorContext";
import { GeneratorForm } from "./components/GeneratorForm";
import { ManifestEditor } from "./components/ManifestEditor";
import { TagReviewDialog } from "./components/TagReviewDialog";

const NO_KEY_TITLE = "An OpenAI API key is required to use this feature";

const SMMGeneratorContent = ({ subcategory }: { subcategory: Subcategory }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    manifest,
    isLoading,
    isGenerating,
    isGeneratingTags,
    isDirty,
    hasOpenAIKey,
    handleGenerateTags,
    cancelGenerateTags,
  } = useSMMGeneratorContext();

  const { setNavigationGuard } = useSettingsContext();
  const confirm = useConfirm();

  const navigateToOpenAI = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isDirty) {
      setNavigationGuard(null);
      return;
    }
    setNavigationGuard(async () =>
      confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes to your manifest. If you leave, your changes will be lost.",
        customActions: ({ confirm: proceed, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="space-x-4">
            <Button variant="ghost" onClick={cancel}>Stay</Button>
            <Button variant="destructive" onClick={proceed}>Leave anyway</Button>
          </div>
        ),
      })
    );
    return () => setNavigationGuard(null);
  }, [isDirty, confirm, setNavigationGuard]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!manifest && !isGenerating) {
    return (
      <>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
                <p className="text-sm text-muted-foreground">{subcategory.description}</p>
              </div>

              {!hasOpenAIKey && (
                <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg mb-5 max-w-3xl">
                  <Lock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      OpenAI API key required
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      An API key is needed to generate manifests and tags.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={navigateToOpenAI} className="shrink-0">
                    <Settings className="h-4 w-4 mr-1.5" />
                    Configure
                  </Button>
                </div>
              )}

              <div className="max-w-3xl">
                <GeneratorForm />
              </div>
            </div>
          </div>
        </div>
        <OpenAIKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  const tagsDisabled = !hasOpenAIKey || isGeneratingTags || isDirty || isLoading || isGenerating;
  const tagsHint = !hasOpenAIKey
    ? NO_KEY_TITLE
    : isDirty
      ? "Save the manifest before generating tags"
      : undefined;

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Page header */}
      <div className="shrink-0 p-6 pb-0">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-2xl font-bold text-foreground">{subcategory.label}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{subcategory.description}</p>
          </div>
          {!hasOpenAIKey && (
            <Button variant="outline" size="sm" onClick={navigateToOpenAI} className="gap-1.5 text-xs text-amber-500 border-amber-500/30 hover:bg-amber-500/5 shrink-0 mt-1">
              <Lock className="h-3.5 w-3.5" />
              Configure API Key
            </Button>
          )}
        </div>
      </div>

      {/* Generate Tags banner */}
      <div className={cn(
        "shrink-0 mx-6 mb-3 rounded-lg border px-4 py-3",
        isGeneratingTags ? "border-border bg-muted/30"
          : isDirty ? "border-amber-500/30 bg-amber-500/5"
          : "border-primary/20 bg-primary/5",
      )}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
              isGeneratingTags ? "bg-muted" : isDirty ? "bg-amber-500/10" : "bg-primary/10",
            )}>
              {isGeneratingTags ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Sparkles className={cn("h-4 w-4", isDirty ? "text-amber-500" : "text-primary")} />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground leading-tight">
                {isGeneratingTags ? "Generating Tags..." : "Generate Marketing Tags"}
              </p>
              <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                {isGeneratingTags
                  ? "Analyzing your manifest to derive tags. This may take a moment."
                  : isDirty
                    ? "Save your manifest changes before generating tags."
                    : "Derive problem, solution, and meta tags from your manifest."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isGeneratingTags && (
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelGenerateTags}
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleGenerateTags}
              disabled={tagsDisabled}
              title={tagsHint}
              className={cn(
                "gap-1.5",
                isDirty && !isGeneratingTags && "bg-amber-500 hover:bg-amber-600 text-white border-amber-500",
              )}
            >
              {isGeneratingTags ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Tags className="h-3.5 w-3.5" />
              )}
              Generate Tags
            </Button>
          </div>
        </div>
      </div>

      {/* Editor card with integrated sidebar */}
      <div className="flex-1 min-h-0 mx-6 mb-6 rounded-lg border border-border bg-card overflow-hidden">
        <ManifestEditor />
      </div>

      <TagReviewDialog />
    </div>
  );
};

const SMMGeneratorDetail = ({ subcategory }: SubcategoryDetailProps) => (
  <SMMGeneratorProvider>
    <SMMGeneratorContent subcategory={subcategory} />
  </SMMGeneratorProvider>
);

export default SMMGeneratorDetail;
